import { ITEM_ID_PATTERN, TOOL_BELT_SLOTS } from "./types.ts";

/**
 * The player's tool belt (ADR-142 decision 3b and its note of 2026-10-29): the record a character's belt is kept and sent
 * as, and the one parser every boundary reads it through. The record names things, never grants them: the map server
 * checks every use against the item's tool kind and the bag, so nothing here is read against the rules tables, and an id
 * the running rules lack is kept so a later rules change can restore its slot.
 */

/**
 * The record's version. It moves only when what a slot or the selection means changes.
 */
export const TOOL_BELT_RECORD_VERSION = 1;

/**
 * The most bytes of a record's JSON text, the bound `characters_tool_belt_shape` checks on the column's text and past
 * which the parser answers unreadable. A well-formed record reaches 234 at most.
 */
export const MAX_TOOL_BELT_RECORD_BYTES = 512;

/**
 * One slot: empty, or the item id of the tool put on it.
 */
export type ToolBeltSlot = string | null;

/**
 * A player's tool belt: its version, exactly `TOOL_BELT_SLOTS` slots, slot 1 first, and the selected slot from 1, or 0
 * for none (the tool put away).
 */
export interface ToolBeltRecord {
  readonly v: typeof TOOL_BELT_RECORD_VERSION;
  readonly slots: readonly ToolBeltSlot[];
  readonly selected: number;
}

/**
 * What `parseToolBeltRecord` answers: the record, or unreadable with why.
 */
export type ToolBeltRead = { readonly ok: true; readonly record: ToolBeltRecord } | { readonly ok: false; readonly reason: string };

/**
 * The record's fields, the only ones a readable record has.
 */
const RECORD_FIELDS = ["v", "slots", "selected"] as const;

/**
 * Reads a value as a tool belt record, as the column's driver or a frame's parse decoded it. It never throws: a value
 * whose JSON text is past `MAX_TOOL_BELT_RECORD_BYTES` bytes or that JSON cannot write, a version it does not know, a
 * field it does not know, a slot count other than `TOOL_BELT_SLOTS`, an id out of the item pattern or a selection out of
 * 0 to `TOOL_BELT_SLOTS` is unreadable. Duplicates, empty slots, a selected empty slot and ids the rules lack are
 * readable.
 *
 * @param value The decoded value.
 * @returns The record, sharing no array with `value`, or unreadable with the first reason found.
 * @example
 * parseToolBeltRecord({ v: 1, slots: ["sickle", null, null, null], selected: 1 }).ok;
 * // true
 */
export function parseToolBeltRecord(value: unknown): ToolBeltRead {
  let text: string | undefined;
  try {
    text = JSON.stringify(value) as string | undefined;
  } catch {
    text = undefined;
  }
  if (text === undefined) {
    return unreadable("the value has no JSON text");
  }
  if (text.length > MAX_TOOL_BELT_RECORD_BYTES) {
    return unreadable(`the value is ${text.length} characters, past ${MAX_TOOL_BELT_RECORD_BYTES}`);
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return unreadable("the value is not an object");
  }
  const unknown = Object.keys(value).find((field) => !(RECORD_FIELDS as readonly string[]).includes(field));
  if (unknown !== undefined) {
    return unreadable(`the field ${JSON.stringify(unknown)} is not a tool belt record's`);
  }
  const { v, slots, selected } = value as { readonly v?: unknown; readonly slots?: unknown; readonly selected?: unknown };
  if (v !== TOOL_BELT_RECORD_VERSION) {
    return unreadable(`the version ${String(v)} is not ${TOOL_BELT_RECORD_VERSION}`);
  }
  if (!Array.isArray(slots) || slots.length !== TOOL_BELT_SLOTS) {
    return unreadable(`the slots are not a list of ${TOOL_BELT_SLOTS}`);
  }
  if (typeof selected !== "number" || !Number.isInteger(selected) || selected < 0 || selected > TOOL_BELT_SLOTS) {
    return unreadable(`the selection is not a whole number from 0 to ${TOOL_BELT_SLOTS}`);
  }
  const read: ToolBeltSlot[] = [];
  for (let index = 0; index < TOOL_BELT_SLOTS; index += 1) {
    const slot = slots[index] as unknown;
    if (slot !== null && (typeof slot !== "string" || !ITEM_ID_PATTERN.test(slot))) {
      return unreadable(`slot ${index + 1} is not empty or an item id`);
    }
    read.push(slot);
  }
  return { ok: true, record: { v: TOOL_BELT_RECORD_VERSION, slots: read, selected } };
}

/**
 * The belt a character holds until it puts a tool on: no tool on any slot and none selected.
 *
 * @returns The record.
 */
export function emptyToolBelt(): ToolBeltRecord {
  return { v: TOOL_BELT_RECORD_VERSION, slots: new Array<ToolBeltSlot>(TOOL_BELT_SLOTS).fill(null), selected: 0 };
}

/**
 * Whether a belt is the empty one, which a character's record keeps as no column at all.
 *
 * @param record The belt.
 * @returns True when no slot holds a tool and none is selected.
 */
export function isEmptyToolBelt(record: ToolBeltRecord): boolean {
  return record.selected === 0 && record.slots.every((slot) => slot === null);
}

/**
 * The item id of the selected tool.
 *
 * @param record The belt.
 * @returns The id, or null when none is selected or the selected slot is empty.
 */
export function selectedTool(record: ToolBeltRecord): string | null {
  return record.selected === 0 ? null : (record.slots[record.selected - 1] ?? null);
}

/**
 * The selection step after a slot: none, slot 1, 2, 3, 4, none again (the page skips empty slots).
 *
 * @param selected The selection now, 0 to `TOOL_BELT_SLOTS`.
 * @returns The next.
 */
export function nextToolSelection(selected: number): number {
  return selected >= TOOL_BELT_SLOTS ? 0 : selected + 1;
}

/**
 * An unreadable answer.
 *
 * @param reason Why.
 * @returns The answer.
 */
function unreadable(reason: string): ToolBeltRead {
  return { ok: false, reason };
}
